import { useState } from "react";
import {
  ActionIcon,
  Button,
  Group,
  Modal,
  Stack,
  Text,
  Textarea,
  Tooltip,
} from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { notifications } from "@mantine/notifications";
import { IconLifebuoy } from "@tabler/icons-react";
import { useTranslation } from "react-i18next";
import { useAssistantStore } from "./useAssistant";

export default function AssistantEscalateButton() {
  const { t } = useTranslation();
  const conversationId = useAssistantStore((s) => s.conversationId);
  const isFlagged = useAssistantStore((s) => s.isFlagged);
  const flagConversation = useAssistantStore((s) => s.flagConversation);

  const [opened, { open, close }] = useDisclosure(false);
  const [reason, setReason] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const disabled = !conversationId || isFlagged;
  const tooltip = !conversationId
    ? t("assistant.escalate.tooltipNoConversation")
    : isFlagged
      ? t("assistant.escalate.tooltipAlreadyFlagged")
      : t("assistant.escalate.button");

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      await flagConversation(reason.trim() || null);
      notifications.show({
        color: "green",
        autoClose: 4000,
        title: t("assistant.escalate.successTitle"),
        message: t("assistant.escalate.successMessage"),
      });
      setReason("");
      close();
    } catch (error) {
      console.error("Escalation failed:", error);
      notifications.show({
        color: "red",
        autoClose: 6000,
        title: t("assistant.escalate.errorTitle"),
        message: t("assistant.escalate.errorMessage"),
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <Tooltip label={tooltip} position="bottom" withArrow>
        <ActionIcon
          variant="subtle"
          size="sm"
          disabled={disabled}
          onClick={open}
          aria-label={t("assistant.escalate.button")}
        >
          <IconLifebuoy size={14} />
        </ActionIcon>
      </Tooltip>

      <Modal
        opened={opened}
        onClose={close}
        title={t("assistant.escalate.dialogTitle")}
        centered
        size="md"
      >
        <Stack gap="sm">
          <Text size="sm" c="dimmed">
            {t("assistant.escalate.dialogDescription")}
          </Text>
          <Textarea
            label={t("assistant.escalate.reasonLabel")}
            placeholder={t("assistant.escalate.reasonPlaceholder")}
            value={reason}
            onChange={(e) => setReason(e.currentTarget.value)}
            minRows={3}
            maxRows={6}
            autosize
            disabled={submitting}
          />
          <Group justify="flex-end" gap="xs" mt="sm">
            <Button variant="default" onClick={close} disabled={submitting}>
              {t("generic.action_cancel")}
            </Button>
            <Button onClick={handleSubmit} loading={submitting}>
              {t("assistant.escalate.submit")}
            </Button>
          </Group>
        </Stack>
      </Modal>
    </>
  );
}
